import { Col, Form, Input, Row } from "antd";
import modal from "antd/es/modal";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectCompany from "../../../../components/selects/SelectCompany";
import { EntCompanyWarehouses } from "../../../../models/chemicalization/companyWarehouses";
import { GeoJsonPoint } from "../../../../models/geoJsonPoint";
import { Geometry } from "../../../../models/geoJson";
import { DraggableModal } from "../../../../components/DraggableModal";
import { MapAddSingleMarker } from "../../../../components/maps/MapAddPoint";
import SelectTerritorialUnitsCode from "../../../../components/selects/SelectTerritorialUnitsCode";

enum FormField {
  id,
  warehouseName,
  fio,
  phone,
  companyId,
  regionId,
  areaId,
  territorialUnit,
  address,
  longitude,
  latitude,
}

interface IProps {
  show: boolean;
  companyWarehouses: EntCompanyWarehouses;
  onHide: () => void;
  onSubmit: (pos: EntCompanyWarehouses) => Promise<boolean>;
}

export const AddCompanyWarehousesModal = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [geoJsonData, setGeoJsonData] = useState<GeoJsonPoint|null>(null);

  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);

  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) setFieldsFromEntity();
	setSelectedRegionId(props.companyWarehouses.territorialUnitRegion?.id || null);
	setSelectedAreaId(props.companyWarehouses.territorialUnitArea?.id || null);
	setSelectedAreaCode(props.companyWarehouses.territorialUnitArea?.code || null);
  }, [props.show]);


  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.warehouseName, value: props.companyWarehouses.warehouseName },
	  { name: FormField.fio, value: props.companyWarehouses.fio },
	  { name: FormField.phone, value: props.companyWarehouses.phone },
	  { name: FormField.companyId, value: props.companyWarehouses.company?.id },
	  { name: FormField.regionId, value: props.companyWarehouses.territorialUnitRegion?.id },
	  { name: FormField.areaId, value: props.companyWarehouses.territorialUnitArea?.id },
	  { name: FormField.territorialUnit, value: props.companyWarehouses.territorialUnit?.id },
	  { name: FormField.address, value: props.companyWarehouses.address },
	]);
  };

  const updateGeoJsonPoint = () => {
	const latitude = props.companyWarehouses.latitude;
	const longitude = props.companyWarehouses.longitude;

	if (latitude !== undefined && latitude !== null && longitude !== undefined && longitude !== null) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t("breeding_farms.error_geometry"));
	}
  };

  useEffect(() => {
	if (props.companyWarehouses.latitude && props.companyWarehouses.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.companyWarehouses.latitude, props.companyWarehouses.longitude]);

  const setEntityFromFields = (): EntCompanyWarehouses => {
	let res = _.cloneDeep(props.companyWarehouses);
	res.warehouseName = form.getFieldValue(FormField.warehouseName);
	res.fio = form.getFieldValue(FormField.fio);
	res.phone = form.getFieldValue(FormField.phone);
	res.companyId = form.getFieldValue(FormField.companyId);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnit);
	res.address = form.getFieldValue(FormField.address);
	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	return res;
  };

  const hideModal = () => {
	props.onHide();
	setSelectedRegionId(null);
	setSelectedRegionPolygonJson(null);
	setSelectedAreaCode(null);
	setGeoJsonData(null);
  };

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  if (geoJsonData !== null) {
		const data = setEntityFromFields();
		const result = await props.onSubmit(data);
		if (result) hideModal();
	  } else {
		modal.error({ title: t("msg.error"), content: t("breeding_farms.choose_location") });
	  }
	} finally {
	  setLoading(false);
	}
  };

  return (
	<DraggableModal
	  title={ props.companyWarehouses.id === null ? t("general.add") : t("general.edit") }
	  visible={ props.show }
	  onCancel={ hideModal }
	  onSubmit={ handleSubmit }
	  submitLoading={ loading }
	  width={ 800 }
	>
	  <Form initialValues={ { remember: true } } autoComplete="off" layout="vertical" form={ form }>
		<Row style={ { margin: 0 } } gutter={ 16 }>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label={ t("titles.company") }
			  name={ FormField.companyId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectCompany/>
			</Form.Item>
			<Form.Item
			  label={ t("chemicalization.company_warehouses.warehouse_name") }
			  name={ FormField.warehouseName }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input type="text"/>
			</Form.Item>
			<Form.Item
			  label={ t("general.fio") }
			  name={ FormField.fio }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input type="text"/>
			</Form.Item>
			<Form.Item
			  label={ t("user.phone_number") }
			  name={ FormField.phone }
			  rules={ [
				{ required: true, message: t("msg.fill_in_the_field") },
				{ pattern: /^996\d{9}$/, message: t("user.phone_number_desc") },
			  ] }
			>
			  <Input
				count={ { show: true, max: 12 } }
				maxLength={ 12 }
				placeholder="996777000000"
				type="number"
				autoComplete="off"
			  />
			</Form.Item>
		  </Col>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label={ t("mechanisation.total_account.address") }
			  name={ FormField.address }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input type="text"/>
			</Form.Item>
			<Form.Item
			  label={ t('titles.territorial_unit_area') }
			  name={ FormField.regionId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectTerritorialUnitsCode
				parentId={ 1 }
				onChange={ (value, option: any) => {
				  setSelectedRegionId(Number(value));
				  setSelectedRegionPolygonJson(option?.data?.polygonJson);
				  setSelectedRegionCode(option?.data?.code);
				  setSelectedAreaCode(null);
				  setSelectedAreaId(null);
				  form.setFieldsValue({
					[FormField.regionId]: value,
					[FormField.areaId]: null, // Сбрасываем район
					[FormField.territorialUnit]: null, // Сбрасываем населенный пункт
				  });
				} }
			  />
			</Form.Item>
			  <>
				{ selectedRegionId || props.companyWarehouses.territorialUnitRegion ? (
				  <Form.Item
					label={ t('titles.territorialUnitDistricts') }
					name={ FormField.areaId }
					rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				  >
					<SelectTerritorialUnitsCode
					  type={ ['AREA', 'CITY_OF_REPUBLICAN_SIGNIFICANCE'] }
					  parentId={ selectedRegionId }
					  onChange={ (value, option: any) => {
						setSelectedRegionPolygonJson(option?.data?.polygonJson);
						setSelectedAreaCode(option?.data?.code || null);
						setSelectedAreaId(value as number);
						form.setFields([{ name: FormField.territorialUnit, value: null }]);
					  } }
					/>
				  </Form.Item>
				) : null }

			{ selectedAreaCode || props.companyWarehouses.territorialUnit ? (
			  <Form.Item
				label={ t("titles.territorialUnitVillage") }
				name={ FormField.territorialUnit }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
			  </Form.Item>
			) : null }
			  </>
		  </Col>
		</Row>
		<MapAddSingleMarker polygonJson={ selectedRegionPolygonJson } setGeoJsonData={ setGeoJsonData }
							geoJsonData={ geoJsonData }/>
	  </Form>
	</DraggableModal>
  );
};